// 繁體中文(非官方翻譯・機械翻訳。ネイティブ確認推奨)。曲名などの固有名詞は原文のまま。
import type { SiteDict } from "./ja";
import type { RelativeDate } from "../../../utils/postMeta";

const zhHant: SiteDict = {
  site: {
    titleSuffix: "!Legit - Reol 非官方粉絲網站",
    topTitle: "!Legit｜收錄 Reol 所有歌曲、演唱會、歌單的非官方粉絲網站",
    defaultDescription: (years: number) =>
      `Reol(れをる)的非官方粉絲網站。收錄所有歌曲的現場演出統計、歷年演唱會歌單、MV 取景地(聖地)地圖、粉絲類型測驗等，可橫向搜尋 ${years} 年來的活動紀錄。`,
    breadcrumbHome: "首頁",
  },
  header: {
    search: "搜尋",
    searchTitle: "橫向搜尋歌曲、演唱會、取景地",
    toDark: "切換為深色模式",
    toLight: "切換為淺色模式",
    myPage: "我的頁面",
    menu: "選單",
    language: "語言",
  },
  footer: {
    officialSite: "官方網站",
    goods: "周邊商品",
    disclaimer:
      "本網站為非官方粉絲網站，與 Reol 官方無任何關係。歌曲與影片僅使用官方嵌入及連結。",
    operator: "營運者(非官方粉絲網站)",
    privacy: "隱私權政策(日文)",
    mtNotice: "本頁由日文機器翻譯而成。若發現翻譯錯誤或不自然的表達，歡迎告訴我們：",
    mtContact: "X @twilightplc",
  },
  video: {
    expand: "將影片恢復為全寬顯示",
    close: "關閉影片",
    unmute: "取消靜音",
    mute: "靜音",
    play: "播放影片",
  },
  home: {
    heroLine1Before: "認識 Reol 的",
    heroLine1Accent: "過去",
    heroLine1After: "，",
    heroLine2Indent: "認識 Reol 的",
    heroLine2Accent: "未來",
    heroLine2After: "。",
    heroEmMobile: 8.6,
    heroEmWide: 8.6,
    heroLead1: "歌曲、演唱會、歌單、取景地。從「れをる」時期至今，",
    heroLead2: "同時也是引導你前往官方內容的非官方資料庫。",
    ticketCta: "前往官方網站購票 →",
    welcomeTitle: "初次認識 Reol",
    welcomeDesc: "代表曲與各時期導覽，從哪個時期入坑都不會迷路。",
    liveTitle: "去看演唱會",
    liveDesc: (n: number) => `${n} 場演出的歌單資料庫與初次參加指南。`,
    liveGuideLink: "閱讀初次參加指南 →",
    dataTitle: "深入挖掘資料",
    dataDesc: (n: number) => `收錄全部 ${n} 首歌曲的演出次數、首次演出、最近演出。`,
    timemachineTitle: "時光機",
    timemachineDesc: "用年月滑桿重現當時的歌曲數、演出場數、演出次數。",
    cgraphTitle: "創作者關係圖",
    cgraphDesc: "將參與歌曲製作的創作者之間的關係視覺化。",
    heatmapTitle: "演出地點地圖",
    heatmapDesc: "依都道府縣以顏色區分演出場數的熱度地圖。",
    sorterTitle: "歌曲排序器",
    sorterDesc: "每次二選一的淘汰賽，決定你最喜歡的一首歌。",
    grammarTitle: "歌單文法",
    grammarDesc: "以數據分析歌曲的前後關係、固定位置、各年度傾向。",
    compareTitle: "歌單比較",
    compareDesc: "比較兩場演出的歌單，將共同曲目、差異、曲序變化視覺化。",
    tourHeatmapTitle: "巡演熱度圖",
    tourHeatmapDesc: "橫跨一次巡演的所有場次，一覽各歌曲是否演出與曲序。",
    similarityTitle: "歌單相似度排行",
    similarityDesc: "不同巡演之間歌單特別相似的場次組合排行。",
    postsTitle: "相關貼文",
    postsDesc: "依時間順序橫向顯示歌曲、演唱會、各場次的相關貼文。",
    quizTitle: "Reol 粉絲類型測驗",
    quizDesc: "回答 20 道題目，測出你的粉絲類型！",
    guideTitle: "演唱會參加指南",
    guideDesc: "從攜帶物品、穿著、禮儀到遠征的訣竅",
  },
  explore: {
    kicker: "EXPLORE — 主要內容",
    discography: "作品與所有歌曲資訊",
    live: "演出資訊、歌單",
    place: "取景地地圖",
    photo: "相簿",
  },
  otd: {
    yesterday: "昨天",
    dayBeforeYesterday: "前天",
    daysAgo: (n: number) => `${n} 天前`,
    tomorrow: "明天",
    dayAfterTomorrow: "後天",
    daysLater: (n: number) => `${n} 天後`,
    releaseSuffix: " 發行",
    releaseTitle: (title: string) => `《${title}》`,
    kickerMonth: "ON THIS MONTH — 本月的紀事",
    kickerDay: "ON THIS DAY — 今天是什麼日子",
    yearsAgoToday: (years: number) => `${years} 年前的今天`,
    anniversaryIn: (when: string, years: number) => `${when}滿 ${years} 週年`,
    anniversaryWas: (when: string, years: number) => `${when}是 ${years} 週年`,
    monthEntry: (md: string, years: number) => `${md}(${years} 年前)`,
    upcoming: (days: number, years: number) => `再過 ${days} 天滿 ${years} 週年`,
    seeAll: "查看這一天的所有紀錄 →",
  },
  common: {
    postTiming: (kind: "live" | "release", rel: RelativeDate): string => {
      const ref = kind === "live" ? "演出" : "發行";
      if (rel.unit === "same") return `${ref}當天`;
      const unit = rel.unit === "day" ? "天" : rel.unit === "month" ? "個月" : "年";
      return `${ref}${rel.direction === "before" ? "前" : "後"} ${rel.amount} ${unit}`;
    },
    postCategory: { 本人: "本人", 公式: "官方", メディア: "媒體" } as Record<string, string>,
    showMore: (n: number) => `顯示更多(${n} 則)`,
    backToTop: "回到頁面頂端",
    backHome: "返回 HOME",
    loading: "載入中...",
    loadError: (err: string) => `載入失敗：${err}`,
  },
  discography: {
    intro: "DISCOGRAPHY 依時間軸收錄至今的作品資訊與翻唱影片等。",
    metaDescription: "依時間軸收錄 Reol 至今的作品資訊與翻唱影片等，也能查看各歌曲的連結。",
    collectionLabel: "收藏(已擁有/已觀看)",
    collectionHint: "點選各卡片的「✓」即可記錄已擁有/已觀看(僅儲存在此裝置的瀏覽器中)",
    count: (n: number) => `${n} 筆`,
    unknownYear: "不明",
    helpFilter: "點選上方的主題標籤即可簡單篩選列表",
    helpFilterExample: "例：點選「#れをる」只會顯示「れをる」名義的資訊",
    helpPlayBefore: "點選標題左側的",
    helpPlayAfter: "即可在畫面上方的播放器播放影片",
    helpOpenBefore: "點選「",
    helpOpenAfter: "展開」即可顯示收錄曲目與各歌曲的連結",
    helpCommentBefore: "歌曲名稱右側若顯示「",
    helpCommentAfter: "」，點選即可查看 Spotify 連結與歌曲分析資訊",
    detail: "詳細",
    detailTitle: "查看歌曲頁面(演出統計、MV、相關貼文)",
    markOwned: "標記為已擁有/已觀看",
    unmarkOwned: "從已擁有中移除",
    owned: "已擁有",
    ownedCount: (n: number) => `${n} 人已擁有/已觀看`,
    tracks: "收錄曲目",
    copied: "已複製",
    copyTitles: "複製曲目",
    interview: "訪談",
    relatedPosts: "相關貼文",
  },
  formats: {
    SG: "單曲",
    配信SG: "數位單曲",
    EP: "EP",
    miniAL: "迷你專輯",
    fullAL: "專輯",
    "DVD/BD": "DVD/BD",
    MV: "MV",
    歌ってみた: "翻唱",
    LP: "黑膠",
  },
  live: {
    metaDescription: "Reol 至今參與的個人演唱會、巡演、音樂祭等資訊，收錄各場演出的歌單、報導與相關貼文。",
    intro1: "LIVE 收錄 Reol 至今參與的個人演唱會、巡演、音樂祭等資訊。",
    intro2: "收錄各場演出的歌單、相關貼文與演出報導，即使沒能參加的場次，也能感受到當時的氛圍。",
    attendanceLabel: "參加紀錄",
    attendanceHint: "點選各演出卡片的「✓」即可記錄已參加(僅儲存在此裝置的瀏覽器中)",
    heardLabel: "現場聽過的歌曲",
    heardHint: "依已標記參加的演出歌單統計(以曾在現場演出過的歌曲為對象)",
    heardList: (n: number) => `聽過的歌曲 (${n})`,
    unheardList: (n: number) => `還沒在現場聽過的歌曲 (${n}，依演出次數排序)`,
    ongoing: "舉辦中",
    upcoming: "近期預定",
    helpFilter: "點選上方的主題標籤即可簡單篩選列表",
    helpFilterExample: "例：點選「#れをる」只會顯示「れをる」名義的資訊",
    helpDetailBefore: "點選「",
    helpDetailAfter: " 詳細」即可顯示演出報導、舉辦地點、歌單、相關貼文等資訊",
    tagEvent: "活動演出",
    tagOneman: "個人演唱會",
    count: (n: number) => `${n} 筆`,
    unknownYear: "不明",
    setlistCopyTitle: (title: string) => `${title} 歌單`,
    markAttended: "標記為已參加",
    unmarkAttended: "從已參加中移除",
    attended: "已參加",
    attendedInYear: (n: number) => `已參加 ${n} 場`,
    showCount: (n: number) => `${n} 場公演`,
    songCount: (n: number) => `${n} 首`,
    detailTitle: "查看這場演出的頁面(歌單、會場、相關貼文)",
    detail: "詳細",
    youtubePlaylist: "YouTube 播放清單",
    attendedCount: (n: number) => `${n} 人已參加`,
    setlist: "歌單",
    copied: "已複製",
    copy: "複製",
    songPageTitle: "查看歌曲頁面",
    songStatsTitle: "在歌曲統計查看演出紀錄",
    noSetlist: "目前尚未登錄歌單資訊",
    toShowPage: "查看演出頁面",
    spoilerBadgeTitle: "巡演進行中，歌單含有劇透",
    spoilerBadge: "⚠ 劇透注意",
    expand: "展開",
    spoilerNoticeTitle: "⚠ 劇透注意",
    spoilerNotice1: "這次巡演目前正在進行中。",
    spoilerNotice2: "歌單與相關貼文含有劇透內容。",
    spoilerNotice3: "請確認後再瀏覽。",
    showSpoiler: "了解劇透風險並顯示",
    relatedPosts: "相關貼文",
  },
  song: {
    shareText: (song: string) => `Reol〈${song}〉的演出統計、MV、串流連結 | !Legit(非官方粉絲網站)`,
    totalPlays: "累計演出",
    playsValue: (n: string) => `${n} 次`,
    firstPlayed: "首次演出",
    lastPlayed: "最近演出",
    album: "收錄作品：",
    lyrics: "作詞",
    music: "作曲",
    stream: "串流收聽",
    lyricsLink: "查看歌詞(歌ネット，日文)",
    watchMv: "觀看官方 MV",
    articles: "關於這首歌的文章(日文)",
    shareTitle: "在 X 分享這首歌的頁面",
    share: "在 X 分享",
    historyKicker: (n: string) => `PERFORMANCE HISTORY — ${n} 筆`,
    noHistoryTitle: "目前還沒有現場演出紀錄",
    noHistoryDesc: "今後在演唱會上演出時，演出紀錄會新增於此。",
    venueTbd: "會場未定",
    closeVideo: "關閉",
    watchVideo: "觀看影片",
    relatedPosts: "相關貼文",
    toStatsTitle: "前往歌曲統計",
    toStatsDesc: "一次查看所有歌曲的演出次數、首次演出、最近演出。",
    toSearchTitle: "前往橫向搜尋",
    toSearchDesc: "橫跨歌曲、演唱會、取景地的關鍵字搜尋。",
    backToTop: "回到首頁",
    metaLead: (song: string) => `Reol〈${song}〉的現場演出統計。`,
    metaPlays: (n: number, first: string | null) => `累計演出 ${n} 次，首次演出為 ${first ?? "不明"}。`,
    metaNoPlays: "尚未確認到現場演出紀錄。",
    metaTail: "收錄演出紀錄、官方 MV、串流連結。",
    breadcrumbStats: "歌曲統計",
  },
  show: {
    setlistCopyTitle: (title: string) => `${title} 歌單`,
    shareText: (heading: string) => `Reol「${heading}」的歌單、會場資訊 | !Legit(非官方粉絲網站)`,
    venue: "會場",
    venueSite: "查看會場網站",
    reliveTitle: "Relive Player (β) — 用本機音源播放這份歌單",
    relive: "Relive Player (β，需自備音源)",
    shareTitle: "在 X 分享這場演出的頁面",
    share: "在 X 分享",
    mapTitle: (heading: string) => `會場地圖 - ${heading}`,
    setlistKicker: (n: number) => `SETLIST — ${n} 首`,
    mcCount: (n: number) => `(MC ${n})`,
    copied: "已複製",
    copy: "複製",
    noSetlistTitle: "目前尚未登錄歌單資訊",
    noSetlistDesc: "確認資訊後將會補上。",
    closeVideo: "關閉",
    watchVideo: "觀看影片",
    venueTbd: "會場未定",
    relatedPosts: "相關貼文",
    toArchiveTitle: "前往 LIVE 資料庫",
    toArchiveDesc: "一次查看歷年演唱會的歌單、演出資訊。",
    toSearchTitle: "前往橫向搜尋",
    toSearchDesc: "橫跨歌曲、演唱會、取景地的關鍵字搜尋。",
    backToTop: "回到首頁",
    metaLead: (name: string) => `Reol「${name}」的演出資訊。`,
    metaVenue: (place: string) => `會場：${place}。`,
    metaSetlist: (n: number) => `收錄 ${n} 首歌單。`,
  },
  stats: {
    title: "歌曲統計",
    metaDescription: "Reol 所有歌曲的現場演出統計，可一覽累計演出次數、首次演出日、最近演出日。",
    lead: "一覽演唱會上演出過的歌曲之累計演出次數、首次/最近演出日。",
    rulesLink: "關於統計規則 →",
    totalPlays: "演出次數(累計)",
    uniqueSongs: "演出過的歌曲",
    searchPlaceholder: "🔍 以歌名搜尋",
    sortPlays: "演出次數(多)",
    sortLast: "最近演出(新)",
    sortFirst: "首次演出(舊)",
    sortName: "歌名",
    songCount: (n: string) => `${n} 首`,
    toSongPageTitle: "前往歌曲頁面",
    firstPlayed: "首次演出",
    lastPlayed: "最近",
    toAlbum: "查看收錄作品 →",
    toSongPage: "前往這首歌的頁面 →",
    watchMv: "▶ 觀看官方 MV",
    stream: "♪ 串流收聽",
    history: "演出紀錄",
    historyCount: (n: number) => `(${n} 筆)`,
    noMatch: "找不到符合的歌曲",
    backToTop: "← 回到首頁",
    historyLoadError: "無法載入演出紀錄",
    historyLoading: "正在載入演出紀錄...",
    historyEmpty: "沒有演出紀錄",
  },
};

export default zhHant;
